import { RefreshCw, RotateCcw } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useWrapRequest } from 'use-wrap-request';
import { ConfirmationModal } from '@components/confirmation-modal';
import { PageHeader } from '@components/page-header';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { Api } from '@core/api';
import { DeadLetterQueueDto } from '@core/api/MobileClient';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';

function getErrorMessage(error: any): string {
    return (
        error?.error?.message?.defaultMessage ??
        error?.error?.defaultMessage ??
        (error instanceof Error ? error.message : 'Unexpected error')
    );
}

const deadLetterCount = (queue: DeadLetterQueueDto) => queue.messageCount ?? 0;

const isMissing = (queue: DeadLetterQueueDto) =>
    queue.messageCount === undefined || queue.messageCount === null;

function byDeadLettersThenMissingThenName(
    first: DeadLetterQueueDto,
    second: DeadLetterQueueDto
): number {
    return (
        deadLetterCount(second) - deadLetterCount(first) ||
        Number(isMissing(second)) - Number(isMissing(first)) ||
        (first.name ?? '').localeCompare(second.name ?? '')
    );
}

const DeadLetterQueues = observer(() => {
    const { mobileClient } = injectTSDI(Api);

    const [queuesToRetry, setQueuesToRetry] = React.useState<
        DeadLetterQueueDto[] | null
    >(null);
    const [retryingQueue, setRetryingQueue] = React.useState<string | null>(
        null
    );

    const queues = useWrapRequest(
        async () => {
            const response =
                await mobileClient.supportApi.deadLetterQueueSupportControllerGetOverview();
            return [...response.data].sort(byDeadLettersThenMissingThenName);
        },
        { defaultData: [] as DeadLetterQueueDto[] }
    );

    const reload = () => {
        queues.request([]);
    };

    React.useEffect(reload, []);

    const queuesWithDeadLetters = queues.$.filter(
        (queue) => deadLetterCount(queue) > 0
    );
    const totalDeadLetters = queuesWithDeadLetters.reduce(
        (total, queue) => total + deadLetterCount(queue),
        0
    );

    const retryQueue = async (queue: DeadLetterQueueDto) => {
        const deadLettersAtStart = deadLetterCount(queue);
        let movedMessages = 0;
        while (movedMessages < deadLettersAtStart) {
            const { data } =
                await mobileClient.supportApi.deadLetterQueueSupportControllerRetry(
                    { deadLetterQueue: queue.name! }
                );
            if (!data.movedMessages) {
                break;
            }
            movedMessages += data.movedMessages;
        }
        return movedMessages;
    };

    const handleRetryConfirmed = async () => {
        if (!queuesToRetry) {
            return false;
        }
        let movedMessages = 0;
        try {
            for (const queue of queuesToRetry) {
                setRetryingQueue(queue.name!);
                movedMessages += await retryQueue(queue);
            }
            toast.success(
                `Moved ${movedMessages} dead letter(s) back for reprocessing`
            );
            setQueuesToRetry(null);
            return true;
        } catch (e: any) {
            toast.error(getErrorMessage(e));
            return false;
        } finally {
            setRetryingQueue(null);
            reload();
        }
    };

    const retrying = retryingQueue !== null;

    return (
        <>
            <PageHeader
                title="Dead Letter Queues"
                badges={
                    <Badge
                        variant={totalDeadLetters > 0 ? 'warning' : 'success'}
                    >
                        {totalDeadLetters} dead letter(s) in{' '}
                        {queuesWithDeadLetters.length} queue(s)
                    </Badge>
                }
                actions={
                    <>
                        <Button
                            variant="outline"
                            loading={queues.loading}
                            disabled={retrying}
                            onClick={reload}
                        >
                            <RefreshCw />
                            Refresh
                        </Button>
                        <Button
                            disabled={retrying || totalDeadLetters === 0}
                            onClick={() =>
                                setQueuesToRetry(queuesWithDeadLetters)
                            }
                        >
                            <RotateCcw />
                            Retry all
                        </Button>
                    </>
                }
            />

            <Alert variant="info" className="mb-4">
                <AlertDescription>
                    Messages a consumer failed to process wait in its dead
                    letter queue. Retry moves each one back to the queue and
                    partition it came from, so the consumer processes it again.
                    Messages that fail again return here. Wallet API queues are
                    not listed; retry purchases on the Purchase Event DLQ page.
                </AlertDescription>
            </Alert>

            <div className="w-full overflow-auto rounded-xl border bg-card">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Dead letter queue</TableHead>
                            <TableHead style={{ width: 160 }}>
                                Dead letters
                            </TableHead>
                            <TableHead style={{ width: 140 }}>
                                Actions
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {queues.$.map((queue) => (
                            <TableRow key={queue.name}>
                                <TableCell>
                                    <code className="text-xs">
                                        {queue.name}
                                    </code>
                                </TableCell>
                                <TableCell>
                                    {isMissing(queue) ? (
                                        <Badge variant="warning">
                                            Queue missing
                                        </Badge>
                                    ) : deadLetterCount(queue) > 0 ? (
                                        <Badge variant="destructive">
                                            {queue.messageCount}
                                        </Badge>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">
                                            0
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-warning"
                                        loading={retryingQueue === queue.name}
                                        disabled={
                                            retrying ||
                                            deadLetterCount(queue) === 0
                                        }
                                        onClick={() =>
                                            setQueuesToRetry([queue])
                                        }
                                    >
                                        <RotateCcw />
                                        Retry
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <ConfirmationModal
                open={Boolean(queuesToRetry)}
                title="Confirm Retry"
                buttonText="Retry"
                submitHandler={handleRetryConfirmed}
                onModalClose={() => setQueuesToRetry(null)}
            >
                <p className="text-sm">
                    This moves{' '}
                    {(queuesToRetry ?? []).reduce(
                        (total, queue) => total + deadLetterCount(queue),
                        0
                    )}{' '}
                    dead letter(s) from {queuesToRetry?.length ?? 0} queue(s)
                    back for reprocessing.
                </p>
                <Alert variant="warning">
                    <AlertDescription>
                        Retry only after the cause of the failures is fixed. Old
                        events are processed after newer ones, and live messages
                        wait behind the retried ones.
                    </AlertDescription>
                </Alert>
            </ConfirmationModal>
        </>
    );
});

export default DeadLetterQueues;
